"use client"

/**
 * AlbumStripAdWrapper
 *
 * Wraps the album thumbnail strip to inject MediaGalleryAdSlot thumbnails
 * at configurable positions. This keeps ad logic out of booru-detail-modal.tsx.
 *
 * Usage: Replace the raw albumPages.map() in the thumbnail strip with this component.
 */

import { useEffect, useMemo, useState } from "react"
import { useAdStore } from "@/lib/ad-store"
import { useHasConsent } from "@/components/cookie-consent"
import { isAdTestModeOn } from "@/lib/ad-cooldown"
import { isGlobalAdsOff } from "@/lib/global-ads-off"
import {
  DEFAULT_GALLERY_AD_CONFIG,
  getAlbumAdIndices,
  MediaGalleryAdSlot,
  type GalleryAdConfig,
} from "@/components/ads/MediaGalleryAdSlot"
import { useTranslations } from "next-intl"

// Cached config
let _galleryConfig: GalleryAdConfig | null = null
let _galleryConfigFetch: Promise<GalleryAdConfig> | null = null

async function fetchGalleryConfig(): Promise<GalleryAdConfig> {
  if (_galleryConfig) return _galleryConfig
  if (_galleryConfigFetch) return _galleryConfigFetch
  _galleryConfigFetch = fetch("/api/admin/gallery-ad-config")
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => {
      const cfg = { ...DEFAULT_GALLERY_AD_CONFIG, ...(d ?? {}) }
      _galleryConfig = cfg
      // Refresh every 5 minutes
      setTimeout(() => { _galleryConfig = null; _galleryConfigFetch = null }, 5 * 60 * 1000)
      return cfg
    })
    .catch(() => { _galleryConfigFetch = null; return DEFAULT_GALLERY_AD_CONFIG })
  return _galleryConfigFetch
}

interface AlbumStripAdWrapperProps {
  /** Total number of real album pages */
  totalPages: number
  /** Current album page index (real index, not virtual) */
  currentPage: number
  /** Render function for a real page thumbnail */
  renderThumbnail: (realIndex: number) => React.ReactNode
  /** Called when an ad slot thumbnail is clicked (navigates to ad view) */
  onAdSlotClick?: (virtualIndex: number) => void
  /** Virtual index of the ad slot currently shown in the main viewer, for highlighting */
  activeAdSlot?: number | null
}

/**
 * Hook that provides the virtual index mapping for the main viewer.
 * Call this in the parent to know if the current page is an ad.
 */
export function useGalleryAdIndices(totalPages: number) {
  const { isPremiumUser } = useAdStore()
  // Force Ads test mode escape hatch — see the matching comment on
  // NetworkDisplayAd.tsx. Admin/super_admin accounts always read
  // isPremiumUser: true, so without this, Force Ads could never actually
  // force album-strip ad slots to show for its only realistic tester.
  const effectivePremiumUser = (isPremiumUser || isGlobalAdsOff()) && !isAdTestModeOn()
  const hasConsent = useHasConsent()
  const [config, setConfig] = useState<GalleryAdConfig>(DEFAULT_GALLERY_AD_CONFIG)

  useEffect(() => {
    if (effectivePremiumUser || !hasConsent) return
    fetchGalleryConfig().then(setConfig)
  }, [effectivePremiumUser, hasConsent])

  const adIndices = useMemo(() => {
    if (effectivePremiumUser || !hasConsent || !config.enabled) return new Set<number>()
    return getAlbumAdIndices(totalPages, config)
  }, [totalPages, config, effectivePremiumUser, hasConsent])

  return { adIndices, config }
}

export function AlbumStripAdWrapper({
  totalPages,
  currentPage,
  renderThumbnail,
  onAdSlotClick,
  activeAdSlot,
}: AlbumStripAdWrapperProps) {
  const t = useTranslations("albumStripAdWrapper")
  const { isPremiumUser } = useAdStore()
  const effectivePremiumUser = (isPremiumUser || isGlobalAdsOff()) && !isAdTestModeOn()
  const hasConsent = useHasConsent()
  const [config, setConfig] = useState<GalleryAdConfig>(DEFAULT_GALLERY_AD_CONFIG)

  useEffect(() => {
    if (effectivePremiumUser || !hasConsent) return
    fetchGalleryConfig().then(setConfig)
  }, [effectivePremiumUser, hasConsent])

  const adIndices = useMemo(() => {
    if (effectivePremiumUser || !hasConsent || !config.enabled) return new Set<number>()
    return getAlbumAdIndices(totalPages, config)
  }, [totalPages, config, effectivePremiumUser, hasConsent])

  // If no ads, just render thumbnails directly
  if (adIndices.size === 0) {
    return (
      <>
        {Array.from({ length: totalPages }, (_, i) => renderThumbnail(i))}
      </>
    )
  }

  // Build virtual index list: real pages + ad slots interleaved
  const virtualCount = totalPages + adIndices.size
  const items: React.ReactNode[] = []
  let realIdx = 0

  for (let vIdx = 0; vIdx < virtualCount; vIdx++) {
    if (adIndices.has(vIdx)) {
      items.push(
        <div
          key={`ad-${vIdx}`}
          onClick={() => onAdSlotClick?.(vIdx)}
          role="button"
          tabIndex={0}
          aria-label={t("viewAd")}
          className={activeAdSlot === vIdx ? "rounded ring-2 ring-purple-400" : undefined}
        >
          <MediaGalleryAdSlot
            slotIndex={vIdx}
            variant="thumb"
            cooldownMinutes={config.cooldown_minutes}
            className="flex-shrink-0"
            onDismiss={() => {}}
          />
        </div>
      )
    } else {
      items.push(renderThumbnail(realIdx))
      realIdx++
    }
  }

  return <>{items}</>
}
